import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import CustomButton from "../components/CustomButton";
import FileInput from "../../srcWeb/components/FileInput/FileInput";
import { getBase64 } from "../utils/helpers";

// Required for react-modal: avoid "App element is not defined" and "Cannot register modal instance that's already open"
if (typeof document !== "undefined") {
  const root = document.getElementById("root");
  if (root) Modal.setAppElement(root);
}

const EditAfterReworkModal = ({
  isModalVisible,
  setIsModalVisible,
  approveHandler,
  voucherData = {},
}) => {
  const [comment, setComment] = useState(
    voucherData?.comment || ""
  );
  const [attachment, setAttachment] = useState(null);
  const [attachmentName, setAttachmentName] = useState("");
  const [error, setError] = useState({
    comment: false,
    attachment: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Reset form when modal opens/closes
    if (isModalVisible.isOpen) {
      setComment(voucherData?.comment || "");
      setAttachment(null);
      setAttachmentName("");
      setError({ comment: false, attachment: false });
    }
  }, [isModalVisible.isOpen, voucherData]);

  const onRequestClose = () => {
    setIsModalVisible({ ...isModalVisible, isOpen: false });
  };

  const handleFileChange = async (files) => {
    if (files && files.length > 0) {
      const file = files[0];
      setAttachmentName(file.name);
      try {
        const base64 = await getBase64(file);
        setAttachment(base64);
        setError((prev) => ({ ...prev, attachment: false }));
      } catch (error) {
        // console.error("Error converting file to base64:", error);
        setError((prev) => ({
          ...prev,
          attachment: true,
        }));
      }
    }
  };

  const hasChanges = () => {
    const commentChanged = comment !== (voucherData?.comment || "");
    const attachmentChanged = attachment !== null;
    return commentChanged || attachmentChanged;
  };

  const handleSubmit = async () => {
    // Validate that at least one field has changed
    if (!hasChanges()) {
      setError({
        comment: true,
        attachment: true,
      });
      return;
    }

    setIsSubmitting(true);

    const payload = {
      voucherId: voucherData?.vid || voucherData?.id,
    };

    // Only include fields that have changed
    if (comment !== (voucherData?.comment || "")) {
      payload.comment = comment;
    }
    if (attachment) {
      payload.attachment = attachment;
      payload.attachmentName = attachmentName;
    }

    approveHandler(payload, () => {
      setIsSubmitting(false);
    });
  };

  return (
    <div className="flex justify-center items-center w-full">
      <Modal
        isOpen={isModalVisible.isOpen}
        onRequestClose={onRequestClose}
        style={{
          content: {
            display: "flex",
            justifyContent: "center",
            borderRadius: "1rem",
            background: "#fff",
            padding: "1.5rem",
            width: "90%",
            maxWidth: "40rem",
            position: "relative",
            maxHeight: "90vh",
            overflowY: "auto",
          },
          overlay: {
            background: "#000000d6",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1,
          },
        }}
      >
        <div className="w-full">
          {/* Header */}
          <div className="flex justify-between relative w-[100%] text-[2rem] font-semibold mb-4">
            Edit Voucher After Rework
            <div
              className="text-[3rem] cursor-pointer text-red-500 ml-4"
              onClick={onRequestClose}
            >
              [x]
            </div>
          </div>

          {/* Comment */}
          <div className="mt-4">
            <div className="font-size-small text-[var(--blackColor)] mb-2">
              Comment
            </div>
            <textarea
              className="h-40 w-full border text-[2rem] mt-2 p-2 rounded-[0.4rem] font-size-medium"
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
                setError((prev) => ({ ...prev, comment: false }));
              }}
              placeholder="Enter comment..."
            />
            {error.comment && !hasChanges() && (
              <div className="font-size-small text-[var(--textRed)] mt-2">
                Please change at least one field
              </div>
            )}
          </div>

          {/* Attachment */}
          <div className="mt-4">
            <div className="font-size-small text-[var(--blackColor)] mb-2">
              Attachment
            </div>
            <FileInput
              onChangeInput={handleFileChange}
              isMultiple={false}
            />
            {attachmentName && (
              <div className="font-size-small text-[var(--blackColor)] mt-2">
                Selected: {attachmentName}
              </div>
            )}
            {error.attachment && !hasChanges() && (
              <div className="font-size-small text-[var(--textRed)] mt-2">
                Please change at least one field
              </div>
            )}
          </div>

          {/* Error message if no changes */}
          {!hasChanges() && (error.comment || error.attachment) && (
            <div className="font-size-small text-[var(--textRed)] mt-4">
              Please modify at least one field (Comment or Attachment) to proceed.
            </div>
          )}

          {/* Submit Button */}
          <div className="w-full flex justify-end mt-6">
            <CustomButton
              title={isSubmitting ? "SUBMITTING..." : "SUBMIT"}
              wrapperClassName="!px-10"
              disabled={isSubmitting}
              onClick={handleSubmit}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EditAfterReworkModal;
